import React, { useContext, useEffect, useState } from 'react';
import { Container, Alert } from '@paypalcorp/pp-react';
import DynamicLayout from '@MerchantConfigurator/components/messaging/Preview/DynamicLayout/DynamicLayout';
import PreviewControls from '@MerchantConfigurator/components/messaging/Preview/PreviewControls';
import { ConfigValues, elementIDs, PreviewModes, Sizes } from '@MerchantConfigurator/constants';
import { getPreviewLayout } from '@MerchantConfigurator/constants/messaging/previewLayouts';
import { ApplicationContext } from '@MerchantConfigurator/context';
import { getPriceOfferTypesList } from '@MerchantConfigurator/utils/messaging';
import styles from './PreviewSection.module.less';
import PropTypes from 'prop-types';

const MessagePreviewWrapper = ({ children }) => {
  const backgroundColor = 'rgba(255, 255, 255, .1)';

  return (
    <div
      style={{
        height: '600px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexFlow: 'column',
        // Required so we "cut off" the flex banner when the message viewer is in
        // collapsed mode
        overflow: 'hidden',
        backgroundColor,
        borderRadius: '8px',

        // Required for the position: absolute that is applied to <MessageArea />
        // when the message viewer is in collapsed mode
        position: 'relative',
        '@media(max-width: 500px)': {
          marginLeft: Sizes.xs,
          marginRight: Sizes.xs
        }
      }}
    >
      {children}
    </div>
  );
};

export const MessageArea = ({ children }) => {
  //Flexing the container width as per the selected messaging config.
  const containerWidth = '100%';

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '512px',
        width: containerWidth
      }}
    >
      {children}
    </div>
  );
};

export const MobileContentContainer = ({ children }) => {
  return (
    <div style={{ border: '11px solid black', borderRadius: '44px', width: '294px' }}>
      {children}
    </div>
  );
};

export const MessageAreaForMobile = ({ children }) => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '492px',
        marginBottom: Sizes.xl,
        marginTop: Sizes['2xl']
      }}
    >
      {children}
    </div>
  );
};

const PreviewSection = ({ activePlacementIndex, config, updateCounters }) => {
  const { localeConfig, previewEnbaledPlacements } = useContext(ApplicationContext);

  const { country } = localeConfig;

  const [previewMode, setPreviewMode] = useState(PreviewModes.mobile);
  const [darkMode, setDarkMode] = useState(false);

  //for the slideshow, auto scrolling the different upstream message variations
  const priceOfferTypeByCountry = getPriceOfferTypesList({ country });
  const [offerType, productPrice] = priceOfferTypeByCountry[0];

  const activePlacement = previewEnbaledPlacements[activePlacementIndex];
  const messageConfig = config[activePlacement];
  const previewLayout = getPreviewLayout({
    activePlacement,
    darkMode,
    previewMode
  });

  const isMessageOnly = previewMode === PreviewModes.message;
  const selectedColor = messageConfig['text-color'];
  useEffect(() => {
    //toggle on dark mode on automatically for color white
    if (selectedColor === ConfigValues.color.white && !darkMode) {
      setDarkMode(true);
    }
    //toggle off dark mode on automatically for color black
    if (selectedColor !== ConfigValues.color.white && !!darkMode) {
      setDarkMode(false);
    }
  }, [selectedColor, activePlacementIndex]);

  return (
    <Container id={elementIDs.previewSectionContainer} style={{ padding: 0 }}>
      <PreviewControls
        darkMode={darkMode}
        previewMode={previewMode}
        setDarkMode={setDarkMode}
        setPreviewMode={setPreviewMode}
        updateCounters={updateCounters}
      />
      <MessagePreviewWrapper isMessageOnly={isMessageOnly} darkMode={darkMode}>
        {previewMode !== PreviewModes.mobile ? (
          <>
            <div className={styles.small_screen_disclaimer}>
              <Alert>View on desktop to customize and preview messaging styles</Alert>
            </div>
            <div className={styles.desktop_configurator_container}>
              <MessageArea messageConfig={messageConfig}>
                <DynamicLayout
                  darkMode={darkMode}
                  layout={previewLayout}
                  messageConfig={messageConfig}
                  offerType={offerType}
                  productPrice={productPrice}
                />
              </MessageArea>
            </div>
          </>
        ) : (
          <MessageAreaForMobile>
            <MobileContentContainer>
              <DynamicLayout
                darkMode={darkMode}
                layout={previewLayout}
                messageConfig={messageConfig}
                offerType={offerType}
                productPrice={productPrice}
              />
            </MobileContentContainer>
          </MessageAreaForMobile>
        )}
      </MessagePreviewWrapper>
    </Container>
  );
};

export default PreviewSection;

PreviewSection.propTypes = {
  activePlacementIndex: PropTypes.number,
  config: PropTypes.object,
  setActivePlacementIndex: PropTypes.func,
  updateCounters: PropTypes.func
};
